import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import zoneService from '../services/zoneService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

interface Zone {
  id: string;
  name: string;
  center_latitude: number;
  center_longitude: number;
  radius_meters: number;
  description?: string;
  color: string;
  is_visible: boolean;
  cpeCount?: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

const ZoneDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [zone, setZone] = useState<Zone | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const loadZone = useCallback(async (zoneId: string) => {
    try {
      setLoading(true);
      const data = await zoneService.getZoneById(zoneId);
      setZone(data);
    } catch (error) {
      console.error('Error loading zone:', error);
      toast.error(t('errors.notFound'));
      navigate('/zones');
    } finally {
      setLoading(false);
    }
  }, [t, navigate]);

  useEffect(() => {
    if (id) {
      loadZone(id);
    }
  }, [id, loadZone]);

  const handleEdit = () => {
    navigate(`/zones/${id}/edit`);
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      await zoneService.deleteZone(id);
      toast.success(t('zone.deleteSuccess'));
      navigate('/zones');
    } catch (error: unknown) {
      console.error('Error deleting zone:', error);
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('errors.generic'));
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const handleRestore = async () => {
    if (!id) return;

    try {
      await zoneService.restoreZone(id);
      toast.success(t('zone.restoreSuccess'));
      if (id) {
        loadZone(id);
      }
    } catch (error: unknown) {
      console.error('Error restoring zone:', error);
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || t('errors.generic'));
    } finally {
      setRestoreDialogOpen(false);
    }
  };

  const handleBack = () => {
    navigate('/zones');
  };

  if (loading) {
    return (
      <>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </>
    );
  }

  if (!zone) {
    return (
      <>
        <Container maxWidth="lg">
          <Typography variant="h6">{t('zone.noZonesFound')}</Typography>
        </Container>
      </>
    );
  }

  const isDeleted = Boolean(zone.deleted_at);

  return (
    <>
      <Container maxWidth="lg">
        <Paper sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" gutterBottom>
                {zone.name}
                {isDeleted && (
                  <Chip
                    label="Deleted"
                    color="error"
                    size="small"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <Chip
                  label={zone.is_visible ? t('zone.fields.isVisible') : 'Hidden'}
                  color={zone.is_visible ? 'success' : 'default'}
                  size="small"
                />
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: zone.color,
                    border: '2px solid',
                    borderColor: 'divider',
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                variant="outlined"
              >
                {t('zone.backToList')}
              </Button>

              {isAdmin && !isDeleted && (
                <>
                  <Button
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    variant="contained"
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    variant="outlined"
                    color="error"
                  >
                    {t('common.delete')}
                  </Button>
                </>
              )}

              {isAdmin && isDeleted && (
                <Button
                  startIcon={<RestoreIcon />}
                  onClick={() => setRestoreDialogOpen(true)}
                  variant="contained"
                  color="success"
                >
                  {t('zone.restoreZone')}
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Zone Details */}
          <Grid container spacing={3}>
            {/* Location */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('zone.fields.centerLatitude')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {zone.center_latitude.toFixed(8)}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                {t('zone.fields.centerLongitude')}
              </Typography>
              <Typography variant="body1">
                {zone.center_longitude.toFixed(8)}
              </Typography>
            </Grid>

            {/* Radius */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('zone.fields.radiusKm')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {(zone.radius_meters / 1000).toFixed(1)} km
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">
                {t('zone.fields.cpeCount')}
              </Typography>
              <Typography variant="body1">
                {zone.cpeCount || 0} {t('cpe.title')}
              </Typography>
            </Grid>

            {/* Description */}
            {zone.description && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('zone.fields.description')}
                </Typography>
                <Typography variant="body1">
                  {zone.description}
                </Typography>
              </Grid>
            )}

            {/* Metadata */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('cpe.metadata')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('zone.fields.createdAt')}
              </Typography>
              <Typography variant="body1">
                {new Date(zone.created_at).toLocaleString()}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('zone.fields.updatedAt')}
              </Typography>
              <Typography variant="body1">
                {new Date(zone.updated_at).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>{t('zone.deleteZone')}</DialogTitle>
          <DialogContent>
            <Typography>{t('zone.confirmDelete')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Restore Confirmation Dialog */}
        <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
          <DialogTitle>{t('zone.restoreZone')}</DialogTitle>
          <DialogContent>
            <Typography>{t('zone.confirmRestore')}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestoreDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleRestore} color="success" variant="contained">
              {t('zone.restoreZone')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ZoneDetailsPage;
